import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import type { DuoMeasurementValues } from "@novorender/api";
import { Box, Button, spacing, Typography } from "@procore/core-react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "app/redux-store-interactions";
import { SlottedWindow } from "components/window/SlottedWindow";
import { type BaseWindowProps } from "contexts/windows";
import { MeasuredObject, MeasuredResult } from "features/measure/measuredObjectProcore";
import { measureActions } from "features/measure/measureSlice";
import { type ExtendedMeasureEntity } from "types/misc";

import { useDimensionData } from "./useDimensionData";

export function DimensionWindow({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { data, loading, error, hasData } = useDimensionData();

    const getEmptyState = () => {
        if (hasData) {
            return undefined;
        }
        return {
            title: t("noDimension"),
            message: t("selectSurfaceToViewDimension"),
        };
    };

    const handlePrevious = () => {
        if (data && data.currentIndex > 0) {
            dispatch(measureActions.selectMeasureSet(data.currentIndex - 1));
        }
    };

    const handleNext = () => {
        if (data && data.currentIndex < data.totalSets - 1) {
            dispatch(measureActions.selectMeasureSet(data.currentIndex + 1));
        }
    };

    const paginationAdornment =
        data && data.totalSets > 1 ? (
            <Box style={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
                <Button
                    icon={<ChevronLeft />}
                    variant="tertiary"
                    size="sm"
                    onClick={handlePrevious}
                    disabled={data.currentIndex === 0}
                    aria-label={t("previous")}
                />
                <Typography intent="small" style={{ minWidth: "60px", textAlign: "center" }}>
                    {data.currentIndex + 1} / {data.totalSets}
                </Typography>
                <Button
                    icon={<ChevronRight />}
                    variant="tertiary"
                    size="sm"
                    onClick={handleNext}
                    disabled={data.currentIndex === data.totalSets - 1}
                    aria-label={t("next")}
                />
            </Box>
        ) : undefined;

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400, height: 500 }}
            minSize={{ width: 200, height: 300 }}
            loading={loading}
            loadingMessage={t("loadingDimension")}
            error={error ? `${t("failedToLoadDimension")}: ${error}` : null}
            emptyState={getEmptyState()}
            onClose={onClose}
            secondaryBgColor
            headerRegion={
                <SlottedWindow.HeaderRow>
                    <SlottedWindow.Title>{t("dimension")}</SlottedWindow.Title>
                    {paginationAdornment}
                </SlottedWindow.HeaderRow>
            }
            data-testid="dimension-window"
        >
            <DimensionContent data={data} />
        </SlottedWindow>
    );
}

interface DimensionData {
    entities: ExtendedMeasureEntity[];
    result: DuoMeasurementValues | undefined;
    currentIndex: number;
    totalSets: number;
}

interface DimensionContentProps {
    data: DimensionData | null;
}

function DimensionContent({ data }: DimensionContentProps) {
    if (!data || (data.entities.length === 0 && !data.result)) {
        return null;
    }

    return (
        <div
            style={{
                flex: 1,
                padding: spacing.lg,
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                overflowY: "auto",
            }}
        >
            {data.entities.map((obj, idx) => (
                <MeasuredObject obj={obj as ExtendedMeasureEntity} idx={idx} key={idx} />
            ))}
            {data.result && <MeasuredResult duoMeasurementValues={data.result} />}
        </div>
    );
}
